import { useCallback } from 'react';
import type { KeyboardEvent, VoidFunctionComponent } from 'react';

import Icon, { icons } from '../Icon';

import styles from './FileTree.module.less';

export type FileTreeNodeArrowProps = {
  /**
   * Whether the directory is open or not.
   */
  isOpen: boolean;
  /**
   * Whether the node is a directory or a file.
   */
  isDirectory: boolean;
  /**
   * Callback for toggling if a directory is open/closed.
   * Only applies to directories.
   */
  onToggleDirectory?: () => void;
};

/**
 * A component that displays an arrow indicating if a directory is open or not.
 * If the file node is not a directory, it will not display the arrow.
 *
 * @param {FileTreeNodeArrowProps} props - The props for the component.
 * @param {boolean} props.isOpen - Whether the directory is open or not.
 * @param {boolean} props.isDirectory - Whether the node is a directory or a file.
 * @param {() => void} [props.onToggleDirectory] - Callback for toggling if a directory is open/closed.
 */
const FileTreeNodeArrow: VoidFunctionComponent<FileTreeNodeArrowProps> = ({
  isOpen,
  isDirectory,
  onToggleDirectory,
}) => {
  //  Ensure that the arrow can be focused and toggled with the keyboard.
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
    if (onToggleDirectory && (event.key === 'Enter' || event.key === ' ')) {
      onToggleDirectory();
    }
  }, [onToggleDirectory]);
  return (
    <span
      role="button"
      tabIndex={0}
      className={styles.treeViewNodeArrow}
      onClick={isDirectory ? onToggleDirectory : undefined}
      onKeyDown={isDirectory ? onKeyDown : undefined}
    >
      {isDirectory && (
        //  Files don't display the arrow.
        //  Note that we still need to display an element here so that the spacing is correct.
        <Icon icon={isOpen ? icons.chevronDown : icons.chevronRight} />
      )}
    </span>
  );
};

export default FileTreeNodeArrow;
